import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  useTheme,
  alpha,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Container,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  StockIssue,
  CreateStockIssueDto,
  UpdateStockIssueDto,
  AvailableStockDto,
} from '../../types/stockIssues.types';
import { stockIssuesService } from '../../services/stockIssues.service';
import { warehousesService } from '../../services/warehouses.service';
import { branchesService } from '../../services/branches.service';
import axiosInstance from '../../services/axiosInstance';
import { itemsService, itemUnitsService } from '../../services/items.service';
import { Warehouse } from '../../types/warehouses.types';
import { Branch } from '../../types/branches.types';
import { Item } from '../../types/items.types';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import ManualCostEditDialog, { ManualCostLineRow } from '../dialogs/ManualCostEditDialog';
import { Build as BuildIcon } from '@mui/icons-material';

const translations = {
  en: {
    title: 'Stock Issue',
    issueDetails: 'Issue Details',
    items: 'Items',
    summary: 'Summary',
    issueNumber: 'Issue #',
    issueDate: 'Date',
    warehouse: 'Source Warehouse',
    branch: 'Destination Branch',
    item: 'Item',
    unit: 'Unit',
    quantity: 'Qty',
    available: 'Available',
    addItem: 'Add Item',
    totalItems: 'Total Items',
    save: 'Save Issue',
    cancel: 'Cancel',
    notes: 'Notes',
    issueSaved: 'Issue saved successfully!',
    issueSaveFailed: 'Failed to save issue',
    insufficientStock: 'Warning: Some items exceed available stock!',
    checkingStock: 'Checking...',
    selectWarehouseFirst: 'Please select warehouse first in Step 1',
  },
  ar: {
    title: 'إصدار مخزون',
    issueDetails: 'بيانات الإصدار',
    items: 'الأصناف',
    summary: 'الملخص',
    issueNumber: 'رقم الإصدار',
    issueDate: 'التاريخ',
    warehouse: 'المخزن المصدر',
    branch: 'الفرع الوجهة',
    item: 'الصنف',
    unit: 'الوحدة',
    quantity: 'الكمية',
    available: 'المتاح',
    addItem: 'إضافة صنف',
    totalItems: 'إجمالي الأصناف',
    save: 'حفظ الإصدار',
    cancel: 'إلغاء',
    notes: 'ملاحظات',
    issueSaved: 'تم حفظ الإصدار بنجاح!',
    issueSaveFailed: 'فشل حفظ الإصدار',
    insufficientStock: 'تحذير: بعض الأصناف تتجاوز الكمية المتاحة!',
    checkingStock: 'جاري الفحص...',
    selectWarehouseFirst: 'يرجى اختيار المخزن أولاً في الخطوة 1',
  },
};

interface StockIssueDialogProps {
  open: boolean;
  onClose: () => void;
  stockIssue?: StockIssue;
  onSave: () => void;
}

interface Unit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
}

interface ItemUnit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
  conversionFactor: number;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: Unit | null;
  availableUnits: ItemUnit[];
  quantity: number;
  notes: string;
  availableStock: AvailableStockDto | null;
  checkingStock: boolean;
}

const StockIssueDialog: React.FC<StockIssueDialogProps> = ({
  open,
  onClose,
  stockIssue,
  onSave,
}) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const userPermissions = useSelector((state: RootState) => state.auth.permissions) ?? [];
  const canOverrideCost = isSuperAdmin || userPermissions.includes('STOCK_COST_OVERRIDE');
  const isEditMode = !!stockIssue;
  const dateLocked = isEditMode && !isSuperAdmin;
  const [manualCostDialogOpen, setManualCostDialogOpen] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [activeStep, setActiveStep] = useState(0);
  const steps = [t.issueDetails, t.items, t.summary];

  const [formData, setFormData] = useState({
    issueNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    warehouseId: null as number | null,
    branchId: null as number | null,
    notes: '',
  });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);

  useEffect(() => {
    if (open) {
      loadMasterData();
      setActiveStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && warehouses.length > 0 && branches.length > 0 && items.length > 0) {
      if (stockIssue) {
        loadStockIssue();
      } else {
        loadNewIssueNumber();
        addNewLine();
      }
    }
  }, [open, stockIssue, warehouses, branches, items]);

  const loadMasterData = async () => {
    try {
      const [warehousesResponse, branchesData, itemsData, settingsData] = await Promise.all([
        axiosInstance.get('/Warehouses?includeMainWarehouses=true&excludeWarehouseBranches=true'),
        branchesService.getAll(),
        itemsService.getAll(),
        axiosInstance.get('/SystemSettings/AllowNegativeStock').catch(() => ({ data: null })),
      ]);

      setAllowNegativeStock(settingsData.data?.value === 'true');
      const warehousesData = warehousesResponse.data;

      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }

      // المخازن: أمين المخزن يرى مخازنه فقط، مدير الفرع والأدمن يرون الكل
      const filteredWarehouses = userWarehouseIds.length > 0
        ? warehousesData.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : warehousesData;
      setWarehouses(filteredWarehouses);

      // الفروع: مدير الفرع يرى أفرعه فقط، أمين المخزن والأدمن يرون الكل — استبعاد فرع المخزن دائماً
      const filteredBranches = (userBranchIds.length > 0
        ? branchesData.filter((b: Branch) => userBranchIds.includes(b.branchId))
        : branchesData).filter((b: Branch) => b.branchType === 'Operational');
      setBranches(filteredBranches);

      setItems(itemsData);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadStockIssue = async () => {
    if (!stockIssue) return;

    setFormData({
      issueNumber: stockIssue.issueNumber,
      issueDate: stockIssue.issueDate.split('T')[0],
      warehouseId: stockIssue.warehouseId,
      branchId: stockIssue.branchId || null,
      notes: stockIssue.notes || '',
    });

    const warehouse = warehouses.find(w => w.warehouseId === stockIssue.warehouseId);
    const branch = stockIssue.branchId ? branches.find(b => b.branchId === stockIssue.branchId) : null;
    
    setSelectedWarehouse(warehouse || null);
    setSelectedBranch(branch || null);

    const loadLinesAsync = async () => {
      const linesPromises = stockIssue.details.map(async (detail) => {
        const item = items.find(i => i.itemId === detail.itemId);
        
        let itemUnits: any[] = [];
        let selectedUnit = null;
        
        try {
          itemUnits = await itemUnitsService.getItemUnits(detail.itemId);
          selectedUnit = itemUnits.find((u: any) => u.unitId === detail.unitId) || null;
        } catch (error) {
          console.error('Failed to load units:', error);
        }

        return {
          id: `${detail.issueDetailId}`,
          itemId: detail.itemId,
          item: item || null,
          unitId: detail.unitId,
          unit: selectedUnit,
          availableUnits: itemUnits,
          quantity: detail.quantity,
          notes: detail.notes || '',
          availableStock: null,
          checkingStock: false,
        };
      });
      
      const lines = await Promise.all(linesPromises);
      setDetailLines(lines);
      
      // Check stock for all loaded lines
      lines.forEach((line) => {
        if (line.itemId && line.unitId && stockIssue.warehouseId) {
          checkStockAvailability(line.id, line.itemId, line.unitId, stockIssue.warehouseId);
        }
      });
    };
    
    loadLinesAsync();
  };

  const loadNewIssueNumber = async () => {
    try {
      const issueNumber = await stockIssuesService.generateIssueNumber();
      setFormData((prev) => ({ ...prev, issueNumber }));
    } catch (error) {
      console.error('Failed to generate issue number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      quantity: 1,
      notes: '',
      availableStock: null,
      checkingStock: false,
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  // ✅ Check stock availability
  const checkStockAvailability = async (
    lineId: string,
    itemId: number,
    unitId: number,
    warehouseId: number
  ) => {
    
    setDetailLines((prev) =>
      prev.map((line) =>
        line.id === lineId ? { ...line, checkingStock: true } : line
      )
    );

    try {
      const availableStock = await stockIssuesService.checkAvailableStock(
        itemId,
        unitId,
        warehouseId
      );
      
      
      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId
            ? { ...line, availableStock, checkingStock: false }
            : line
        )
      );
    } catch (error) {
      console.error('❌ Failed to check stock:', error);
      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId ? { ...line, checkingStock: false, availableStock: null } : line
        )
      );
      toast.error(isRTL ? 'فشل فحص الكمية المتاحة' : 'Failed to check available stock');
    }
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      updateLine(lineId, 'availableStock', null);
      return;
    }

    // Check if warehouse is selected
    if (!formData.warehouseId) {
      toast.warning(t.selectWarehouseFirst);
      console.warn('⚠️ Warehouse not selected');
    }

    try {
      let units = await itemUnitsService.getItemUnits(item.itemId);
      
      let defaultUnit = units.find(u => u.unitId === item.baseUnitId);
      if (!defaultUnit) {
        defaultUnit = units[0];
      }

      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                item,
                itemId: item.itemId,
                availableUnits: units,
                unit: defaultUnit || null,
                unitId: defaultUnit?.unitId || null,
              }
            : line
        )
      );

      // Check stock ONLY if warehouse is selected
      if (defaultUnit && formData.warehouseId) {
        await checkStockAvailability(lineId, item.itemId, defaultUnit.unitId, formData.warehouseId);
      } else {
        console.warn('⚠️ Cannot check stock - missing warehouse or unit');
      }
    } catch (error) {
      console.error('Failed to load item units:', error);
    }
  };

  const handleUnitChange = (lineId: string, unit: Unit | null) => {
    updateLine(lineId, 'unit', unit);
    updateLine(lineId, 'unitId', unit?.unitId || null);

    const line = detailLines.find(l => l.id === lineId);
    if (line && line.itemId && unit && formData.warehouseId) {
      checkStockAvailability(lineId, line.itemId, unit.unitId, formData.warehouseId);
    }
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const calculateTotals = () => {
    const totalItems = detailLines.length;
    const totalQuantity = detailLines.reduce((sum, line) => sum + line.quantity, 0);
    return { totalItems, totalQuantity };
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.warehouseId) newErrors.warehouseId = 'Required';
    if (!formData.branchId) newErrors.branchId = 'Required';
    if (detailLines.length === 0) newErrors.details = 'Required';

    detailLines.forEach((line, index) => {
      if (!line.itemId) newErrors[`item_${index}`] = 'Required';
      if (!line.unitId) newErrors[`unit_${index}`] = 'Required';
      if (line.quantity <= 0) newErrors[`quantity_${index}`] = 'Must be > 0';
      
      // منع الحفظ إذا تجاوزت الكمية المتاح — فقط لو AllowNegativeStock=false
      if (!allowNegativeStock && line.availableStock && line.quantity > line.availableStock.availableQuantity) {
        newErrors[`quantity_${index}`] = isRTL
          ? `الكمية المتاحة فقط ${line.availableStock.availableQuantity}`
          : `Only ${line.availableStock.availableQuantity} available`;
      }
    });

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast.error(isRTL ? 'يرجى تصحيح الأخطاء قبل الحفظ' : 'Please correct errors before saving');
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (stockIssue) {
        const updateDto: UpdateStockIssueDto = {
          issueId: stockIssue.issueId,
          issueDate: formData.issueDate,
          warehouseId: formData.warehouseId!,
          branchId: formData.branchId || undefined,
          status: stockIssue.status,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => {
            const parsedId = parseInt(line.id);
            return {
              issueDetailId: !isNaN(parsedId) && parsedId > 0 && parsedId < 2147483647 ? parsedId : 0,
              lineNumber: index + 1,
              itemId: line.itemId!,
              unitId: line.unitId!,
              quantity: line.quantity,
              notes: line.notes || undefined,
            };
          }),
        };
        await stockIssuesService.update(stockIssue.issueId, updateDto);
      } else {
        const createDto: CreateStockIssueDto = {
          issueNumber: formData.issueNumber,
          issueDate: formData.issueDate,
          warehouseId: formData.warehouseId!,
          branchId: formData.branchId || undefined,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            notes: line.notes || undefined,
          })),
        };
        await stockIssuesService.create(createDto);
      }
      toast.success(t.issueSaved);
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.message || t.issueSaveFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      issueNumber: '',
      issueDate: new Date().toISOString().split('T')[0],
      warehouseId: null,
      branchId: null,
      notes: '',
    });
    setDetailLines([]);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setErrors({});
    setActiveStep(0);
    onClose();
  };

  const totals = calculateTotals();

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={6} sx={{ mb: 3, background: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <ShippingIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formData.issueNumber || '---'}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.issueNumber}</Typography>
                  </Box>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.issueDate}
                    value={formData.issueDate ? new Date(formData.issueDate) : null}
                    onChange={(date) => setFormData({ ...formData, issueDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.issueDate, helperText: errors.issueDate, sx: { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.15)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' }, '& .MuiInputBase-input': { color: 'white' } } } }}
                  />
                </LocalizationProvider>
              </CardContent>
            </Card>

            <Card elevation={3} sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <WarehouseIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6">{t.warehouse}</Typography>
                </Stack>
                <Autocomplete
                  options={warehouses}
                  getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                  value={selectedWarehouse}
                  onChange={(_, value) => {
                    setSelectedWarehouse(value);
                    setFormData({ ...formData, warehouseId: value?.warehouseId || null });
                  }}
                  renderInput={(params) => <TextField {...params} label={t.warehouse} error={!!errors.warehouseId} helperText={errors.warehouseId} />}
                  disabled={!!stockIssue}
                />
              </CardContent>
            </Card>

            <Card elevation={3}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <StoreIcon sx={{ color: theme.palette.success.main }} />
                  <Typography variant="h6">{t.branch}</Typography>
                </Stack>
                <Autocomplete
                  options={branches}
                  getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
                  value={selectedBranch}
                  onChange={(_, value) => {
                    setSelectedBranch(value);
                    setFormData({ ...formData, branchId: value?.branchId || null });
                  }}
                  renderInput={(params) => <TextField {...params} label={t.branch} error={!!errors.branchId} helperText={errors.branchId} />}
                  disabled={!!stockIssue}
                />
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            {!formData.warehouseId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t.selectWarehouseFirst}
              </Alert>
            )}
            
            <Box ref={gridRef}>
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.item}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.unit}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.quantity}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.available}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.notes}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailLines.map((line, index) => {
                    const hasInsufficientStock = !!(line.availableStock && line.quantity > line.availableStock.availableQuantity);
                    
                    return (
                      <TableRow key={line.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ minWidth: 250 }}>
                          <Autocomplete
                            options={items}
                            getOptionLabel={(option) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`}
                            value={line.item}
                            onChange={(_, value) => handleItemChange(line.id, value)}
                            renderInput={(params) => <TextField {...params} size="small" error={!!errors[`item_${index}`]} />}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Autocomplete
                            options={line.availableUnits}
                            getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName}
                            value={line.unit}
                            onChange={(_, value) => handleUnitChange(line.id, value)}
                            renderInput={(params) => <TextField {...params} size="small" error={!!errors[`unit_${index}`]} />}
                            disabled={!line.item}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={line.quantity === 0 ? '' : String(line.quantity)}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                updateLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v));
                              }
                            }}
                            size="small"
                            error={!!errors[`quantity_${index}`] || hasInsufficientStock}
                            inputProps={{ inputMode: 'decimal' }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          {line.checkingStock ? (
                            <Chip icon={<CircularProgress size={16} />} label={t.checkingStock} size="small" color="info" />
                          ) : line.availableStock ? (
                            hasInsufficientStock ? (
                              <Chip icon={<WarningIcon />} label={`${line.availableStock.availableQuantity}`} size="small" color="error" />
                            ) : (
                              <Chip icon={<CheckCircleIcon />} label={`${line.availableStock.availableQuantity}`} size="small" color="success" />
                            )
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={line.notes}
                            onChange={(e) => updateLine(line.id, 'notes', e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => removeLine(line.id)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>

            {detailLines.some(line => line.availableStock && line.quantity > line.availableStock.availableQuantity) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>{t.insufficientStock}</strong>
              </Alert>
            )}

            <Button onClick={addNewLine} startIcon={<AddIcon />} variant="outlined" sx={{ mt: 2 }} fullWidth>
              {t.addItem}
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={6} sx={{ background: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(145deg, #10b981 0%, #059669 100%)', color: 'white', mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <InventoryIcon sx={{ fontSize: 60 }} />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={4}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{totals.totalItems}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalItems}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{totals.totalQuantity}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.quantity}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>{t.issueDetails}</Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.issueNumber}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{formData.issueNumber}</Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.issueDate}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{formData.issueDate}</Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.warehouse}:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedWarehouse ? `${selectedWarehouse.warehouseCode} - ${isRTL ? selectedWarehouse.warehouseNameAr : selectedWarehouse.warehouseName}` : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.branch}:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedBranch ? `${selectedBranch.branchCode} - ${isRTL ? selectedBranch.branchNameAr : selectedBranch.branchName}` : '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>{t.items}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t.item}</TableCell>
                      <TableCell>{t.unit}</TableCell>
                      <TableCell align="right">{t.quantity}</TableCell>
                      <TableCell align="right">{t.available}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLines.map((line, index) => (
                      <TableRow key={line.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{line.item ? `${line.item.itemCode} - ${isRTL ? line.item.itemNameAr : line.item.itemName}` : '-'}</TableCell>
                        <TableCell>{line.unit ? (isRTL ? line.unit.unitNameAr : line.unit.unitName) : '-'}</TableCell>
                        <TableCell align="right">{line.quantity}</TableCell>
                        <TableCell align="right">
                          {line.availableStock ? (
                            line.quantity > line.availableStock.availableQuantity ? (
                              <Typography color="error" fontWeight={600}>{line.availableStock.availableQuantity}</Typography>
                            ) : (
                              <Typography color="success.main" fontWeight={600}>{line.availableStock.availableQuantity}</Typography>
                            )
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {formData.notes && (
              <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" gutterBottom>{t.notes}</Typography>
                <Typography variant="body2">{formData.notes}</Typography>
              </Paper>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth dir={isRTL ? 'rtl' : 'ltr'} PaperProps={{ sx: { minHeight: '95vh', maxHeight: '95vh', background: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <ShippingIcon sx={{ fontSize: 50 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{stockIssue ? 'Edit' : 'New'}</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Stack>
        <LinearProgress variant="determinate" value={((activeStep + 1) / steps.length) * 100} sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#fbbf24' } }} />
      </Box>

      <Box sx={{ px: 3, pt: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'auto' }}>
        <Container maxWidth="xl" disableGutters>
          {renderStepContent()}
        </Container>
      </DialogContent>

      <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button onClick={() => setActiveStep(prev => Math.max(0, prev - 1))} disabled={activeStep === 0} size="large" variant="outlined" sx={{ minWidth: 120 }}>{isRTL ? 'السابق' : 'Back'}</Button>
          <Stack direction="row" spacing={2}>
            {isEditMode && canOverrideCost && stockIssue && (
              <Button
                onClick={() => {
                  if (stockIssue.status === 'Posted') {
                    toast.error(isRTL ? 'ألغِ الترحيل أولاً ثم عدّل التكلفة' : 'Unpost first, then edit cost');
                    return;
                  }
                  setManualCostDialogOpen(true);
                }}
                size="large"
                variant="outlined"
                color="warning"
                startIcon={<BuildIcon />}
                sx={{ minWidth: 180 }}
              >
                {isRTL ? '🔧 تعديل التكلفة يدوياً' : '🔧 Manual Cost'}
              </Button>
            )}
            <Button onClick={handleClose} size="large" variant="outlined" sx={{ minWidth: 120 }}>{t.cancel}</Button>
            {activeStep === steps.length - 1 ? (
              <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />} size="large" sx={{ minWidth: 200, background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)' }}>{t.save}</Button>
            ) : (
              <Button onClick={() => setActiveStep(prev => Math.min(steps.length - 1, prev + 1))} variant="contained" size="large" sx={{ minWidth: 120, background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' }}>{isRTL ? 'التالي' : 'Next'}</Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {isEditMode && stockIssue && (
        <ManualCostEditDialog
          open={manualCostDialogOpen}
          onClose={() => setManualCostDialogOpen(false)}
          onSaved={() => { onSave(); }}
          docType="stockIssue"
          docId={stockIssue.issueId}
          docNumber={stockIssue.issueNumber || ''}
          docStatus={stockIssue.status || 'Draft'}
          lines={(stockIssue.details || []).map((d: any): ManualCostLineRow => ({
            detailId: d.issueDetailId,
            lineLabel: String(d.lineNumber || d.issueDetailId),
            itemCode: d.itemCode || '',
            itemName: isRTL ? (d.itemNameAr || d.itemName) : (d.itemName || d.itemNameAr || ''),
            unitName: isRTL ? (d.unitNameAr || d.unitName) : (d.unitName || d.unitNameAr || ''),
            quantity: d.quantity || 0,
            originalCost: d.isManualCost && d.originalCost != null ? d.originalCost : d.unitCost,
            currentUnitCost: d.unitCost,
            isManualCost: !!d.isManualCost,
            manualCostByName: d.manualCostByName,
            manualCostAt: d.manualCostAt,
          }))}
        />
      )}
    </Dialog>
  );
};

export default StockIssueDialog;